import { DateTime } from 'luxon';
import { action, makeAutoObservable } from 'mobx';
import {
  CalculationApi,
  CalculationApiResponse,
  CalculationStatus,
} from '../../api/CalculationApi';
import { InvoiceType } from '../../config/Constants';
import { DailyExchangeRates } from './ExchangeRates';
import { Posting } from './Posting';
import { Transaction } from './Transaction';

export class Calculation {
  id: number = 0;
  groupId: number = 0;
  month: DateTime = null;
  entityTransactions: Map<number, Transaction[]> = new Map();
  entityRechargeTotals: Map<number, number> = new Map();
  start: DateTime = null;
  end: DateTime = null;
  locked: boolean = false;
  lockDate: DateTime = null;
  version: number = 0;
  parent: number = 0;
  buildStatus: CalculationStatus = CalculationStatus.calculating;
  exchangeRates: DailyExchangeRates = new DailyExchangeRates();
  invoices: Posting[] = [];
  postingDate: DateTime = null;
  hasSecondRecharges: boolean = false;
  supersededBy: number = 0;
  isPartialLoad: boolean = false;

  hasBeenUnlocked: boolean = false;
  hasNewTransactions: boolean = false;
  hasOutdatedPostings: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }

  static fromApi(apiData: CalculationApiResponse) {
    const calc = new Calculation();
    calc.id = apiData.id;
    calc.month = DateTime.fromISO(apiData.end);
    calc.start = DateTime.fromISO(apiData.start);
    calc.end = DateTime.fromISO(apiData.end);
    calc.locked = apiData.locked;
    calc.lockDate = DateTime.fromISO(apiData.lockDate);
    calc.version = apiData.version;
    calc.hasBeenUnlocked = !calc.locked && calc.lockDate.isValid;
    calc.parent = apiData.parent;
    calc.buildStatus = apiData.buildStatus;
    calc.postingDate = DateTime.fromISO(apiData.postingDate);
    calc.hasSecondRecharges = apiData.hasSecondRecharges;
    calc.exchangeRates = DailyExchangeRates.fromApi(apiData.exchangeRates);

    calc.invoices = apiData.postingSummaries.map(p => {
      const posting = Posting.fromApi(p);
      if (posting.isOutdated) calc.hasOutdatedPostings = true;
      return posting;
    });

    return calc;
  }

  entityRechargeTotal = (entityId: number): number => {
    return this.entityRechargeTotals.has(entityId)
      ? this.entityRechargeTotals.get(entityId)
      : 0;
  };

  lock = () =>
    CalculationApi.lock(this.groupId, this.id).then(calculation => {
      this.lockDate = DateTime.fromISO(calculation.lockDate);
      this.locked = true;
      return this;
    });
  
  loadInvoices = (create: boolean = false) =>
    CalculationApi.getInvoices(this.groupId, this.id, create).then(
      action(response => {
        this.hasOutdatedPostings = false;
        this.invoices = response.map(invoice => {
          const posting = Posting.fromApi(invoice);
          posting.groupId = this.groupId;

          if (posting.isOutdated) {
            this.hasOutdatedPostings = true;
          }

          return posting;
        });
      })
    );

  get invoiceSet(): Map<number, number[]> {
    const invoiceSet = new Map();
    this.invoices.forEach(invoice => {
      if (!invoiceSet.has(invoice.fromEntityId)) {
        invoiceSet.set(invoice.fromEntityId, []);
      }
      if (
        !invoiceSet
          .get(invoice.fromEntityId)
          .includes(invoice.toEntityId)
      ) {
        invoiceSet.get(invoice.fromEntityId).push(invoice.toEntityId);
      }
    });

    return invoiceSet;
  } 

  invoice = (from: number, to: number, type: InvoiceType): Posting => {
    return this.invoices.filter(
      invoice =>
        invoice.fromEntityId === from &&
        invoice.toEntityId === to &&
        invoice.invoiceType === type
    )[0];
  };

  loadLatestId = () =>
    CalculationApi.getLatestId(this.groupId, this.id).then(response => {
      if (response.id && response.id !== this.id) {
        this.supersededBy = response.id;
      }
    });
}
